<script setup lang="ts">
import { coingeckoIdFromSelection } from '#core/utils/coingecko-selection'
import { Field, FieldContent, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { useMutation } from '@pinia/colada'

const { registerData, setView, closeAfterSuccess } = useLoginModal()
const auth = useAuth()
const supabase = useSupabaseClient()
const route = useRoute()
const router = useRouter()

const projectNameId = useId()
const tickerId = useId()
const firstNameId = useId()
const lastNameId = useId()
const telegramId = useId()

const { mutateAsync: finalizeRegistration } = useMutation(
  buildApiMutation('/onboarding/finalize-registration', { method: 'post' }),
)

const loading = ref(false)
const errorMessage = ref<string | null>(null)
const emailExists = ref(false)
const finalizing = ref(false)

function buildRegistrationBody() {
  return {
    firstName: registerData.firstName,
    lastName: registerData.lastName,
    telegram: registerData.telegram || undefined,
    tokenStatus: registerData.tokenStatus,
    coingeckoId: coingeckoIdFromSelection(registerData.coin),
    projectName: registerData.tokenStatus === 'listed'
      ? (registerData.coin?.name ?? '')
      : registerData.projectName,
    ticker: registerData.tokenStatus === 'listed'
      ? (registerData.coin?.symbol ?? '')
      : registerData.ticker,
  }
}

function stripFinalizeQuery() {
  if (route.query.auth !== 'finalize') {
    return
  }
  const query = { ...route.query }
  delete query.auth
  router.replace({ path: route.path, query })
}

async function finalizeOAuthUser() {
  loading.value = true
  errorMessage.value = null
  try {
    await finalizeRegistration({ body: buildRegistrationBody() })
    await auth.refresh({ from: 'auth/register-details' })
    loading.value = false
    closeAfterSuccess()
    stripFinalizeQuery()
  }
  catch (error: any) {
    loading.value = false
    finalizing.value = false
    errorMessage.value = error?.message || 'We couldn’t finish setting up your account. Please try again.'
  }
}

const canFinalize = computed(() => {
  if (!registerData.firstName.trim() || !registerData.lastName.trim()) {
    return false
  }
  if (registerData.tokenStatus === 'listed') {
    return !!registerData.coin?.id
  }
  if (registerData.tokenStatus === 'pre-tge') {
    return !!registerData.projectName.trim() && !!registerData.ticker.trim()
  }
  // 'no-token' has no additional required fields.
  return true
})

watch(() => registerData.coin, (coin) => {
  if (coin && coin.id === null) {
    registerData.coin = null
    registerData.tokenStatus = 'pre-tge'
  }
})

async function onFinalize() {
  if (!canFinalize.value || loading.value) {
    return
  }

  if (auth.isLoggedIn) {
    return finalizeOAuthUser()
  }

  loading.value = true
  errorMessage.value = null
  emailExists.value = false
  finalizing.value = true

  const { data, error } = await supabase.auth.signUp({
    email: registerData.email,
    password: registerData.password,
    options: {
      emailRedirectTo: new URL('/auth/finalize', window.location.origin).toString(),
      data: {
        first_name: registerData.firstName,
        last_name: registerData.lastName,
        registration: {
          tokenStatus: registerData.tokenStatus,
          coingeckoId: coingeckoIdFromSelection(registerData.coin),
          projectName: registerData.tokenStatus === 'listed'
            ? (registerData.coin?.name ?? '')
            : registerData.projectName,
          ticker: registerData.tokenStatus === 'listed'
            ? (registerData.coin?.symbol ?? '')
            : registerData.ticker,
          telegram: registerData.telegram || null,
        },
      },
    },
  })

  if (error) {
    // Return to the form so the user can fix the issue.
    finalizing.value = false
    if (/already registered|already exists/i.test(error.message)) {
      emailExists.value = true
      errorMessage.value = 'An account with this email already exists.'
    }
    else {
      errorMessage.value = error.message
    }
    loading.value = false
    return
  }

  if (data.session) {
    return finalizeOAuthUser()
  }

  loading.value = false
  setView('register-finalizing')
}
</script>

<template>
  <AuthRegisterFinalizing v-if="finalizing" state="processing" />

  <div v-else class="flex w-full flex-col gap-5">
    <div>
      <h1 class="text-lg font-semibold text-primal">
        Your Forgd account is ready for activation
      </h1>
      <p class="text-sm text-secondary">
        Share the following details to craft your custom experience.
      </p>
    </div>

    <form class="space-y-4" novalidate @submit.prevent="onFinalize">
      <div class="space-y-2">
        <p class="text-sm text-secondary">
          What is the status of your token
        </p>
        <AuthTokenStatusTabs v-model="registerData.tokenStatus" />
      </div>

      <CoinGeckoSearch
        v-if="registerData.tokenStatus === 'listed'"
        v-model="registerData.coin"
        size="sm"
        class="h-[40px]"
        placeholder="Search token by name or ticker..."
      />

      <div v-else-if="registerData.tokenStatus === 'pre-tge'" class="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel :for="projectNameId">
            Project Name
          </FieldLabel>
          <FieldContent>
            <Input :id="projectNameId" v-model="registerData.projectName" name="projectName" size="md" placeholder="e.g., Forgd Project" />
          </FieldContent>
        </Field>
        <Field>
          <FieldLabel :for="tickerId">
            Token Ticker
          </FieldLabel>
          <FieldContent>
            <Input :id="tickerId" v-model="registerData.ticker" name="ticker" size="md" placeholder="e.g., FRGD" />
          </FieldContent>
        </Field>
      </div>

      <div class="flex items-center gap-3">
        <span class="whitespace-nowrap text-sm text-chart-label">Contact Information</span>
        <div class="h-px flex-1 b-default border-t" />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel :for="firstNameId">
            First name
          </FieldLabel>
          <FieldContent>
            <Input :id="firstNameId" v-model="registerData.firstName" name="firstName" size="md" placeholder="e.g. John" />
          </FieldContent>
        </Field>
        <Field>
          <FieldLabel :for="lastNameId">
            Last name
          </FieldLabel>
          <FieldContent>
            <Input :id="lastNameId" v-model="registerData.lastName" name="lastName" size="md" placeholder="e.g. Doe" />
          </FieldContent>
        </Field>
      </div>

      <Field>
        <FieldLabel :for="telegramId">
          Share your Telegram handle (optional)
        </FieldLabel>
        <FieldContent>
          <Input :id="telegramId" v-model="registerData.telegram" name="telegram" size="md" placeholder="e.g. @johnDoe" />
        </FieldContent>
      </Field>

      <div v-if="errorMessage" class="text-sm text-error">
        {{ errorMessage }}
        <button
          v-if="emailExists"
          type="button"
          class="font-semibold text-accent"
          @click="setView('login')"
        >
          Log in instead
        </button>
      </div>

      <Button
        size="lg"
        class="h-12"
        block
        type="submit"
        :loading="loading"
        :disabled="!canFinalize"
      >
        Finalize
      </Button>
    </form>
  </div>
</template>
